import './settings-leaf.css';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { offlineFallbackCachedAt } from '../../state/offline-cache.ts';
import { companyHasPlanFeature } from '../plan-gate.tsx';
import { useHorizonChange } from '../../hooks/use-horizon-change.ts';
import { Alert, Button, ConfirmDialog, Select } from '../ui.tsx';

export default function RecurringHorizonCard() {
  const { t } = useTranslation();
  const { company, can, refresh, user } = useSession();
  const queryClient = useQueryClient();
  const canonical = company?.recurringHorizonMonths ?? 3;
  const [draft, setDraft] = useState<{ months: 3 | 6 | 9 }>({ months: canonical });
  const adopted = useRef(canonical);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [saved, setSaved] = useState<{
    months: 3 | 6 | 9;
    visitsAdded: number;
    visitsRemoved: number;
    seriesProcessed: number;
    backfillPending: boolean;
  } | null>(null);
  const allowed = Boolean(company && companyHasPlanFeature(company, 'scheduling') && can('company:write'));
  useEffect(() => {
    if (draftRef.current.months === adopted.current) setDraft({ months: canonical });
    adopted.current = canonical;
  }, [canonical]);
  const change = useHorizonChange({
    draft,
    context: JSON.stringify([company?.id, user?.id, canonical]),
    writable: allowed,
    preview: scheduleApi.previewRecurringHorizon,
    save: (input, previewToken) => scheduleApi.updateRecurringHorizon({ ...input, previewToken }),
    onSaved: (result, submitted) => {
      adopted.current = result.months;
      if (draftRef.current === submitted) setDraft({ months: result.months });
      queryClient.removeQueries({ queryKey: ['recurring-horizon-status', company?.id] });
      setSaved(result);
      void refresh();
      void queryClient.invalidateQueries({ queryKey: ['series'] });
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
  const status = useQuery({
    queryKey: ['recurring-horizon-status', company?.id, saved?.months],
    queryFn: scheduleApi.recurringHorizon,
    enabled: allowed && change.online && Boolean(saved?.backfillPending),
    retry: false,
    networkMode: 'always',
    refetchInterval: (query) =>
      offlineFallbackCachedAt(query.state.data) === null && query.state.data?.backfillPending === false
        ? false
        : 10_000,
  });
  const liveStatus = offlineFallbackCachedAt(status.data) === null ? status.data : undefined;
  const outcome = saved && liveStatus?.months === saved.months ? { ...saved, ...liveStatus } : saved;
  if (!allowed) return null;
  const dirty = draft.months !== adopted.current;
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="recurring-horizon-heading">
      <div className="work-section-heading">
        <h2 id="recurring-horizon-heading" className="mb-4 text-lg font-semibold">
          {t('recurringHorizon.companyHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('recurringHorizon.companyDescription')}</p>
      <Select
        label={t('recurringHorizon.companyHeading')}
        value={String(draft.months)}
        error={change.fieldErrors.months}
        options={[3, 6, 9].map((months) => ({
          value: String(months),
          label: t('recurringHorizon.months', { count: months }),
        }))}
        onChange={(event) => {
          change.clear();
          setSaved(null);
          setDraft({ months: Number(event.target.value) as 3 | 6 | 9 });
        }}
      />
      <p className="mb-4 field-hint">
        {t(draft.months === 3 ? 'recurringHorizon.rolling84' : 'recurringHorizon.rolling', { months: draft.months })}
      </p>
      {!change.online ? <Alert tone="info">{t('recurringHorizon.offline')}</Alert> : null}
      {change.error && !change.confirmation ? <Alert tone="error">{change.error}</Alert> : null}
      {outcome ? (
        <Alert tone="success">
          {outcome.backfillPending
            ? t('recurringHorizon.backfillPending')
            : t('recurringHorizon.companySaved', { count: outcome.visitsAdded, series: outcome.seriesProcessed })}
          {outcome.backfillPending && outcome.visitsAdded > 0 ? (
            <p className="mt-2">
              {t('recurringHorizon.progress', { count: outcome.visitsAdded, series: outcome.seriesProcessed })}
            </p>
          ) : null}
          {outcome.visitsRemoved > 0 ? (
            <p className="mt-2">{t('recurringHorizon.removed', { count: outcome.visitsRemoved })}</p>
          ) : null}
        </Alert>
      ) : null}
      {saved && status.data && saved.months !== status.data.months ? (
        <Alert tone="info">{t('recurringHorizon.statusChanged')}</Alert>
      ) : null}
      {saved && (status.isError || offlineFallbackCachedAt(status.data) !== null) ? (
        <Alert tone="error">
          <p>{t('recurringHorizon.statusFailed')}</p>
          <Button
            className="mt-2"
            busy={status.isFetching}
            disabled={!change.online}
            onClick={() => void status.refetch()}
          >
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="primary"
          busy={change.phase !== 'idle'}
          disabled={!dirty || !change.online}
          onClick={change.submit}
        >
          {t('recurringHorizon.saveCompany')}
        </Button>
        {change.phase === 'previewing' ? <Button onClick={change.cancel}>{t('common.cancel')}</Button> : null}
        {dirty && change.phase === 'idle' ? (
          <Button
            onClick={() => {
              change.clear();
              setDraft({ months: canonical });
              setSaved(null);
            }}
          >
            {t('settings.discard')}
          </Button>
        ) : null}
      </div>
      {change.confirmation ? (
        <ConfirmDialog
          title={t('recurringHorizon.confirmTitle')}
          confirmLabel={t('recurringHorizon.continue')}
          busy={change.phase === 'saving'}
          confirmDisabled={!change.online}
          error={change.error}
          onConfirm={change.confirm}
          onCancel={change.cancel}
        >
          <p>
            {t(
              change.confirmation.removeCount > 0 ? 'recurringHorizon.confirmRemoval' : 'recurringHorizon.reviewAgain',
              { count: change.confirmation.removeCount },
            )}
          </p>
          {change.confirmation.protectedCount > 0 ? (
            <p className="mt-3">{t('recurringHorizon.protected', { count: change.confirmation.protectedCount })}</p>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
